import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpRequest } from '@angular/common/http';
import { config } from '../config';

@Injectable({
  providedIn: 'root'
})
export class HttpService {
  private RESTAURANT_ID: string | null;
  private token: string | null;
  private readonly API_URL = config.API_URL;
  private readonly CLOUDINARY_URL = 'https://api.cloudinary.com/v1_1/devoret/image/upload';
  private readonly CLOUDINARY_PRESET = 'hkxdx8rv';

  constructor(private http: HttpClient) {
    this.RESTAURANT_ID = sessionStorage.getItem('restaurant_id');
    this.token = sessionStorage.getItem('token');
  }

  /**
   * Get current auth headers with bearer token
   */
  private getAuthHeaders(): HttpHeaders {
    this.token = sessionStorage.getItem('token'); // Refresh token on each request
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': this.token ? `Bearer ${this.token}` : ''
    });
  }

  /**
   * System user login
   */
  systemUserLogin(data: any) {
    return this.http.post<any>(`${this.API_URL}v1/systemUserLogin`, data);
  }

  // ============= OFFERS & PROMOTIONS =============

  getOffers() {
    const url = `${config.API_URL_ADMIN}offers/${this.RESTAURANT_ID}`;
    return this.http.get(url, { headers: this.getAuthHeaders() });
  }

  getPromotions() {
    const url = `${config.API_URL_ADMIN}promotion/${this.RESTAURANT_ID}`;
    return this.http.get(url, { headers: this.getAuthHeaders() });
  }

  getDiscount() {
    const url = `${config.API_URL_ADMIN}discount/${this.RESTAURANT_ID}`;
    return this.http.get(url, { headers: this.getAuthHeaders() });
  }

  getBookingDeals() {
    const url = `${config.API_URL_ADMIN}getBookingDeals/${this.RESTAURANT_ID}`;
    return this.http.get(url, { headers: this.getAuthHeaders() });
  }

  // ============= SCHEDULE MANAGEMENT =============

  getSchedule() {
    const url = `${config.API_URL_ADMIN}schedule/${this.RESTAURANT_ID}`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders() });
  }

  createSchedule(schedule: any) {
    const data = { restaurant_id: this.RESTAURANT_ID, data: schedule };
    const url = `${config.API_URL_ADMIN}createSchedule`;
    return this.http.post<any>(url, data, { headers: this.getAuthHeaders() });
  }

  updateSchedule(schedule: any) {
    const data = { restaurant_id: this.RESTAURANT_ID, data: schedule };
    const url = `${config.API_URL_ADMIN}createSchedule`;
    return this.http.put<any>(url, data, { headers: this.getAuthHeaders() });
  }

  deleteOrderSchedule(data: any) {
    data.restaurant_id = this.RESTAURANT_ID;
    const headers = this.getAuthHeaders();
    const reqx = new HttpRequest('DELETE', `${config.API_URL_ADMIN}deleteOrderSchedule`, data, {
      reportProgress: true,
      headers: headers
    });
    return this.http.request(reqx);
  }

  deleteReservationSchedule(data: any) {
    data.restaurant_id = this.RESTAURANT_ID;
    const headers = this.getAuthHeaders();
    const reqx = new HttpRequest('DELETE', `${config.API_URL_ADMIN}deleteReservationSchedule`, data, {
      reportProgress: true,
      headers: headers
    });
    return this.http.request(reqx);
  }

  deleteDefaultSchedule(data: any) {
    this.RESTAURANT_ID = sessionStorage.getItem('restaurant_id');
    data.restaurant_id = this.RESTAURANT_ID;
    const headers = this.getAuthHeaders();
    const reqx = new HttpRequest('DELETE', `${config.API_URL_ADMIN}deleteDefaultSchedule`, data, {
      reportProgress: true,
      headers: headers
    });
    return this.http.request(reqx);
  }

  // ============= IMAGE UPLOAD =============

  uploadAnyImage(file: any) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'dnavg0za');
    formData.append('api_key', '839396983344582');
    const headers = new HttpHeaders().set('X-Requested-With', 'XMLHttpRequest');
    const reqx = new HttpRequest('POST', this.CLOUDINARY_URL, formData, {
      reportProgress: true,
      headers: headers
    });
    return this.http.request(reqx);
  }

  // ============= PAYMENT =============

  getPaymentMethod() {
    const url = `${config.API_URL_ADMIN}getPaymentMethod/${this.RESTAURANT_ID}`;
    return this.http.get(url, { headers: this.getAuthHeaders() });
  }

  updatePaymentMethod(data: any) {
    const url = `${config.API_URL_ADMIN}__updatePaymentMethod`;
    return this.http.put(url, data, { headers: this.getAuthHeaders() });
  }

  // ============= CUSTOMER MANAGEMENT =============

  getCustomerList(params: any) {
    let httpParams = new HttpParams();
    if (params['page']) httpParams = httpParams.set('page', params['page']);
    if (params['limit']) httpParams = httpParams.set('numRecord', params['limit']);
    if (params['restaurant_id']) httpParams = httpParams.set('restaurant_id', params['restaurant_id']);
    if (params['userType']) httpParams = httpParams.set('userType', params['userType']);
    if (params['postcode']) httpParams = httpParams.set('postcode', params['postcode']);
    if (params['full_address']) httpParams = httpParams.set('full_address', params['full_address']);
    if (params['created_date']) httpParams = httpParams.set('created_date', params['created_date']);

    const url = `${config.API_URL_ADMIN}getCustomerList`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders(), params: httpParams });
  }

  getTodayUser(page: any) {
    const url = `${config.API_URL_ADMIN}todayRegisteredUsers/${page}`;
    return this.http.get(url, { headers: this.getAuthHeaders() });
  }

  getRestaurantCustomerList() {
    const url = `${config.API_URL_ADMIN}getCustomerList?restaurant_id=${this.RESTAURANT_ID}`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders() });
  }

  customerTransaction(userId: any) {
    const url = `${config.API_URL_ADMIN}customerTransaction/${userId}`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders() });
  }

  getAllCustomerOrders(userId: any) {
    const url = `${this.API_URL}v1/auth/customer_orders/${userId}`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders() });
  }

  // ============= ORDERS =============

  getRestaurantOrders(when: any) {
    const url = `${config.API_URL_ADMIN}orders/${this.RESTAURANT_ID}/${when}`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders() });
  }

  getRestaurantOrdersV2(data: any) {
    data.restaurant_id = this.RESTAURANT_ID;
    const url = `${config.API_URL_ADMIN}orders`;
    return this.http.post<any>(url, data, { headers: this.getAuthHeaders() });
  }

  // ============= RESERVATIONS =============

  getReservationsV2(data: any) {
    data.restaurant_id = this.RESTAURANT_ID;
    const url = `${config.API_URL_ADMIN}booking`;
    return this.http.post<any>(url, data, { headers: this.getAuthHeaders() });
  }

  // ============= ACTIVITY TRACKING =============

  getActivityByUserId(userId: any) {
    const url = `${config.API_URL_ADMIN}getActivityByUserId/${userId}`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders() });
  }

  getUserOrderList(userId: string, page: number = 1, numRecord: number = 20) {
    const params = new HttpParams()
      .set('user_id', userId)
      .set('page', String(page))
      .set('numRecord', String(numRecord));
    const url = `${config.API_URL_ADMIN}userOrderList`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders(), params });
  }

  getUserBookingList(userId: string, page: number = 1, numRecord: number = 20) {
    const params = new HttpParams()
      .set('user_id', userId)
      .set('page', String(page))
      .set('numRecord', String(numRecord));
    const url = `${config.API_URL_ADMIN}userBookingList`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders(), params });
  }

  // ============= ADDRESS LOOKUP =============

  retriveAddressListByPostcode(postcode: any) {
    const params = new HttpParams().set('postcode', postcode);
    const url = `${config.API2}auth/goaddressFind`;
    return this.http.get(url, { headers: this.getAuthHeaders(), params });
  }

  // ============= USER SEARCH & BLOCKING =============

  getSearchUserList(param: string) {
    const params = new HttpParams().set('searchString', param);
    const url = `${config.API_URL_ADMIN}searchUser`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders(), params });
  }

  getUserMonitoringDashboard(query: {
    page?: number;
    numRecord?: number;
    restaurant_id?: string;
    tab?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    isBlocked?: boolean;
    isVerified?: boolean;
  }) {
    let params = new HttpParams();
    if (query.page) params = params.set('page', String(query.page));
    if (query.numRecord) params = params.set('numRecord', String(query.numRecord));
    if (query.restaurant_id) params = params.set('restaurant_id', query.restaurant_id);
    if (query.tab) params = params.set('tab', query.tab);
    if (query.search) params = params.set('search', query.search);
    if (query.sortBy) params = params.set('sortBy', query.sortBy);
    if (query.sortOrder) params = params.set('sortOrder', query.sortOrder);
    if (typeof query.isBlocked === 'boolean') params = params.set('isBlocked', String(query.isBlocked));
    if (typeof query.isVerified === 'boolean') params = params.set('isVerified', String(query.isVerified));

    const url = `${config.API_URL_ADMIN}userMonitoringDashboard`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders(), params });
  }

  updateCustomerStatus(userId: string, enabled: boolean) {
    const url = `${config.API_URL_ADMIN}updateCustomerStatus`;
    return this.http.put<any>(url, { user_id: userId, enabled }, { headers: this.getAuthHeaders() });
  }

  toggleCustomerBlock(payload: { user_id: string; restaurant_id: string; block: boolean }) {
    const url = `${config.API_URL_ADMIN}toggleCustomerBlock`;
    return this.http.post<any>(url, payload, { headers: this.getAuthHeaders() });
  }

  toggleAddressBlock(payload: { restaurant_id: string; postcode?: string; full_address?: string; block: boolean }) {
    const url = `${config.API_URL_ADMIN}toggleAddressBlock`;
    return this.http.post<any>(url, payload, { headers: this.getAuthHeaders() });
  }

  getBlockUserList(query: { restaurant_id?: string; is_active?: boolean | ''; block_type?: string; include_inactive?: boolean } = {}) {
    const url = `${config.API_URL_ADMIN}getBlockUserList`;
    let params = new HttpParams();
    if (query.restaurant_id) { params = params.set('restaurant_id', query.restaurant_id); }
    if (query.block_type) { params = params.set('block_type', query.block_type); }
    if (query.include_inactive) { params = params.set('include_inactive', 'true'); }
    if (query.is_active !== undefined && query.is_active !== '') {
      params = params.set('is_active', String(query.is_active));
    }
    return this.http.get<any>(url, { headers: this.getAuthHeaders(), params });
  }

  addIntoBlockList(data: any) {
    const url = `${config.API_URL_ADMIN}addIntoBlockList`;
    return this.http.post<any>(url, data, { headers: this.getAuthHeaders() });
  }

  updateIntoBlockList(data: any) {
    const url = `${config.API_URL_ADMIN}updateIntoBlockList`;
    return this.http.put<any>(url, data, { headers: this.getAuthHeaders() });
  }

  removeFromBlockList(id: any, reason: string = '') {
    const url = `${config.API_URL_ADMIN}removeFromBlockList?_id=${id}`;
    return this.http.delete<any>(url, { headers: this.getAuthHeaders()});
  }

  disableUser(userId: string) {
    const url = `${config.API_URL_ADMIN}disableUser`;
    return this.http.put<any>(url, { _id: userId }, { headers: this.getAuthHeaders() });
  }

  removeUser(userId: string) {
    const url = `${config.API_URL_ADMIN}removeUser?_id=${userId}`;
    const reqx = new HttpRequest('DELETE', url, {
      reportProgress: true,
      headers: this.getAuthHeaders()
    });
    return this.http.request(reqx);
  }

  // ============= NEW SCHEDULE MANAGEMENT =============

  getNewSchedule() {
    const url = `${config.API_URL_ADMIN}findRestaurantSchedules?restaurant_id=${this.RESTAURANT_ID}`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders() });
  }

  createNewSchedule(schedule: any) {
    schedule.restaurant_id = this.RESTAURANT_ID;
    const url = `${config.API_URL_ADMIN}createNewRestaurantSchedule`;
    return this.http.post<any>(url, schedule, { headers: this.getAuthHeaders() });
  }

  createInitialSchedule(schedule: any) {
    const url = `${config.API_URL_ADMIN}createInitialSchedule?restaurant_id=${this.RESTAURANT_ID}`;
    return this.http.post<any>(url, schedule, { headers: this.getAuthHeaders() });
  }

  updateScheduleForAllDay(schedule: any) {
    const url = `${config.API_URL_ADMIN}updateAllWeekDaysSchedule?restaurant_id=${this.RESTAURANT_ID}`;
    return this.http.post<any>(url, schedule, { headers: this.getAuthHeaders() });
  }

  updateRestaurantSchedule(schedule: any) {
    schedule.restaurant_id = this.RESTAURANT_ID;
    const url = `${config.API_URL_ADMIN}updateRestaurantSchedule?id=${schedule._id}`;
    return this.http.put<any>(url, schedule, { headers: this.getAuthHeaders() });
  }

  removeSchedule(id: any) {
    const url = `${config.API_URL_ADMIN}removeSchedule?id=${id}`;
    return this.http.delete<any>(url, { headers: this.getAuthHeaders() });
  }

  // ============= PAYMENT CONFIGURATION =============

  createPaymentConfig(data: any) {
    const url = `${config.API_URL_ADMIN}createPaymentConfig`;
    return this.http.post<any>(url, data, { headers: this.getAuthHeaders() });
  }

  // ============= DELIVERY PLATFORM INTEGRATION =============

  addIntoDeliveryExternalService(data: any) {
    const url = `${config.API_URL_ADMIN}delivery-external-service/createService`;
    return this.http.post<any>(url, data, { headers: this.getAuthHeaders() });
  }

  getAllServices(restaurantId: string) {
    const url = `${config.API_URL_ADMIN}delivery-external-service/getAllServices/${restaurantId}`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders() });
  }

  removeDeliveryExternalService(serviceId: string) {
    const url = `${config.API_URL_ADMIN}delivery-external-service/deactivateService/${serviceId}`;
    return this.http.delete<any>(url, { headers: this.getAuthHeaders() });
  }

  updateDeliveryExternalService(data: any, serviceId: string) {
    const url = `${config.API_URL_ADMIN}delivery-external-service/updateService/${serviceId}`;
    return this.http.put<any>(url, data, { headers: this.getAuthHeaders() });
  }

  // ============= JUST EAT DaaS (JET GO) =============

  getJustEatDaasConfigs(query: { stage_or_prod?: string; is_active?: boolean } = {}) {
    let params = new HttpParams();
    if (query.stage_or_prod) params = params.set('stage_or_prod', query.stage_or_prod);
    if (typeof query.is_active === 'boolean') params = params.set('is_active', String(query.is_active));
    const url = `${config.API_URL_ADMIN}just-eat-daas/configs`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders(), params });
  }

  getJustEatDaasConfig(restaurantId: string) {
    const url = `${config.API_URL_ADMIN}just-eat-daas/config/${restaurantId}`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders() });
  }

  createJustEatDaasConfig(data: any) {
    const url = `${config.API_URL_ADMIN}just-eat-daas/config`;
    return this.http.post<any>(url, data, { headers: this.getAuthHeaders() });
  }

  updateJustEatDaasConfig(restaurantId: string, data: any) {
    const url = `${config.API_URL_ADMIN}just-eat-daas/config/${restaurantId}`;
    return this.http.put<any>(url, data, { headers: this.getAuthHeaders() });
  }

  switchJustEatDaasEnvironment(restaurantId: string, stageOrProd: string) {
    const url = `${config.API_URL_ADMIN}just-eat-daas/config/${restaurantId}/environment`;
    return this.http.patch<any>(url, { stage_or_prod: stageOrProd }, { headers: this.getAuthHeaders() });
  }

  toggleJustEatDaasConfig(restaurantId: string, isActive?: boolean) {
    const url = `${config.API_URL_ADMIN}just-eat-daas/config/${restaurantId}/toggle`;
    const body = typeof isActive === 'boolean' ? { is_active: isActive } : {};
    return this.http.patch<any>(url, body, { headers: this.getAuthHeaders() });
  }

  toggleJustEatDaasPaymentType(restaurantId: string, enabled?: boolean) {
    const url = `${config.API_URL_ADMIN}just-eat-daas/config/${restaurantId}/payment-type`;
    const body = typeof enabled === 'boolean' ? { payment_type_enabled: enabled } : {};
    return this.http.patch<any>(url, body, { headers: this.getAuthHeaders() });
  }

  deleteJustEatDaasConfig(restaurantId: string) {
    const url = `${config.API_URL_ADMIN}just-eat-daas/config/${restaurantId}`;
    return this.http.delete<any>(url, { headers: this.getAuthHeaders() });
  }

  // ============= SYSTEM ADMIN USER MANAGEMENT =============

  getSystemAdminUsers(query: {
    page?: number;
    numRecord?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    status?: boolean;
    user_role?: number;
    restaurant_id?: string;
  }) {
    let params = new HttpParams();
    if (query.page) params = params.set('page', String(query.page));
    if (query.numRecord) params = params.set('numRecord', String(query.numRecord));
    if (query.search) params = params.set('search', query.search);
    if (query.sortBy) params = params.set('sortBy', query.sortBy);
    if (query.sortOrder) params = params.set('sortOrder', query.sortOrder);
    if (typeof query.status === 'boolean') params = params.set('status', String(query.status));
    if (query.user_role !== undefined && query.user_role !== null) params = params.set('user_role', String(query.user_role));
    if (query.restaurant_id) params = params.set('restaurant_id', query.restaurant_id);

    const url = `${config.API_URL_ADMIN}system-admin-users`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders(), params });
  }

  createSystemAdminUser(data: any) {
    const url = `${config.API_URL_ADMIN}system-admin-users`;
    return this.http.post<any>(url, data, { headers: this.getAuthHeaders() });
  }

  updateSystemAdminUser(id: string, data: any) {
    const url = `${config.API_URL_ADMIN}system-admin-users/${id}`;
    return this.http.put<any>(url, data, { headers: this.getAuthHeaders() });
  }

  deleteSystemAdminUser(id: string) {
    const url = `${config.API_URL_ADMIN}system-admin-users/${id}`;
    return this.http.delete<any>(url, { headers: this.getAuthHeaders() });
  }

  forceRemoveSystemAdminUserAccess(id: string) {
    const url = `${config.API_URL_ADMIN}system-admin-users/${id}/force-remove-access`;
    return this.http.patch<any>(url, {}, { headers: this.getAuthHeaders() });
  }

  getSystemUserActivities(query: {
    page?: number;
    numRecord?: number;
    search?: string;
    restaurant_id?: string;
    created_by?: string;
    platform?: string;
    severity?: string;
    activity_type?: string;
    notification_only?: boolean;
    from?: string;
    to?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    let params = new HttpParams();
    if (query.page) params = params.set('page', String(query.page));
    if (query.numRecord) params = params.set('numRecord', String(query.numRecord));
    if (query.search) params = params.set('search', query.search);
    if (query.restaurant_id) params = params.set('restaurant_id', query.restaurant_id);
    if (query.created_by) params = params.set('created_by', query.created_by);
    if (query.platform) params = params.set('platform', query.platform);
    if (query.severity) params = params.set('severity', query.severity);
    if (query.activity_type) params = params.set('activity_type', query.activity_type);
    if (typeof query.notification_only === 'boolean') params = params.set('notification_only', String(query.notification_only));
    if (query.from) params = params.set('from', query.from);
    if (query.to) params = params.set('to', query.to);
    if (query.sortBy) params = params.set('sortBy', query.sortBy);
    if (query.sortOrder) params = params.set('sortOrder', query.sortOrder);

    const url = `${config.API_URL_ADMIN}admin-activities`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders(), params });
  }

  getAdminActivityUnread(numRecord: number = 8) {
    let params = new HttpParams();
    params = params.set('numRecord', String(numRecord));

    const url = `${config.API_URL_ADMIN}admin-activities/unread`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders(), params });
  }

  markAdminActivityRead(ids: string[]) {
    const url = `${config.API_URL_ADMIN}admin-activities/mark-read`;
    return this.http.patch<any>(url, { ids }, { headers: this.getAuthHeaders() });
  }

  markAllAdminActivityRead() {
    const url = `${config.API_URL_ADMIN}admin-activities/mark-all-read`;
    return this.http.patch<any>(url, {}, { headers: this.getAuthHeaders() });
  }

  createAdminActivity(data: {
    restaurant_id?: string;
    restaurant_name?: string;
    platform?: string;
    activity_type?: string;
    title: string;
    activity_description?: string;
    target_user_id?: string;
    meta?: any;
    is_notification?: boolean;
  }) {
    const url = `${config.API_URL_ADMIN}admin-activities`;
    return this.http.post<any>(url, data, { headers: this.getAuthHeaders() });
  }

  exportAdminActivities(query: {
    format: 'csv' | 'excel';
    search?: string;
    restaurant_id?: string;
    created_by?: string;
    platform?: string;
    severity?: string;
    activity_type?: string;
    notification_only?: boolean;
    from?: string;
    to?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    let params = new HttpParams();
    params = params.set('format', query.format);
    if (query.search) params = params.set('search', query.search);
    if (query.restaurant_id) params = params.set('restaurant_id', query.restaurant_id);
    if (query.created_by) params = params.set('created_by', query.created_by);
    if (query.platform) params = params.set('platform', query.platform);
    if (query.severity) params = params.set('severity', query.severity);
    if (query.activity_type) params = params.set('activity_type', query.activity_type);
    if (typeof query.notification_only === 'boolean') params = params.set('notification_only', String(query.notification_only));
    if (query.from) params = params.set('from', query.from);
    if (query.to) params = params.set('to', query.to);
    if (query.sortBy) params = params.set('sortBy', query.sortBy);
    if (query.sortOrder) params = params.set('sortOrder', query.sortOrder);

    const url = `${config.API_URL_ADMIN}admin-activities/export`;
    return this.http.get(url, { headers: this.getAuthHeaders(), params, responseType: 'blob' as 'json' });
  }

  // ============= WINSTON SYSTEM LOGS =============

  getSystemLogs(query: { limit?: number; skip?: number; level?: string } = {}) {
    let params = new HttpParams();
    if (query.limit) params = params.set('limit', String(query.limit));
    if (query.skip) params = params.set('skip', String(query.skip));
    if (query.level) params = params.set('level', query.level);

    const url = `${config.API_URL_ADMIN}system-logs`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders(), params });
  }

  getErrorLogs(query: { limit?: number; skip?: number } = {}) {
    let params = new HttpParams();
    if (query.limit) params = params.set('limit', String(query.limit));
    if (query.skip) params = params.set('skip', String(query.skip));

    const url = `${config.API_URL_ADMIN}system-logs/errors`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders(), params });
  }

  searchSystemLogs(query: { q: string; limit?: number; skip?: number }) {
    let params = new HttpParams();
    if (query.q) params = params.set('q', query.q);
    if (query.limit) params = params.set('limit', String(query.limit));
    if (query.skip) params = params.set('skip', String(query.skip));

    const url = `${config.API_URL_ADMIN}system-logs/search`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders(), params });
  }

  // ============= CHAIN MANAGEMENT (SUPER ADMIN) =============

  getChainGroups(query: {
    page?: number;
    numRecord?: number;
    searchString?: string;
    status?: boolean;
    group_type?: 'chain' | 'branch-group' | '';
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    let params = new HttpParams();
    if (query.page) params = params.set('page', String(query.page));
    if (query.numRecord) params = params.set('numRecord', String(query.numRecord));
    if (query.searchString) params = params.set('searchString', String(query.searchString));
    if (typeof query.status === 'boolean') params = params.set('status', String(query.status));
    if (query.group_type) params = params.set('group_type', query.group_type);
    if (query.sortBy) params = params.set('sortBy', query.sortBy);
    if (query.sortOrder) params = params.set('sortOrder', query.sortOrder);

    const url = `${config.API_URL}v1/auth/admin/chain-groups`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders(), params });
  }

  createChainGroup(data: any) {
    const url = `${config.API_URL}v1/auth/admin/chain-groups`;
    return this.http.post<any>(url, data, { headers: this.getAuthHeaders() });
  }

  updateChainGroup(chainId: string, data: any) {
    const url = `${config.API_URL}v1/auth/admin/chain-groups/${chainId}`;
    return this.http.put<any>(url, data, { headers: this.getAuthHeaders() });
  }

  archiveChainGroup(chainId: string) {
    const url = `${config.API_URL}v1/auth/admin/chain-groups/${chainId}`;
    return this.http.delete<any>(url, { headers: this.getAuthHeaders() });
  }

  restoreChainGroup(chainId: string) {
    const url = `${config.API_URL}v1/auth/admin/chain-groups/${chainId}/restore`;
    return this.http.put<any>(url, {}, { headers: this.getAuthHeaders() });
  }

  getChainGroupDetails(chainId: string) {
    const url = `${config.API_URL}v1/auth/admin/chain-groups/${chainId}`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders() });
  }

  searchChainRestaurants(query: { page?: number; numRecord?: number; searchString?: string }) {
    let params = new HttpParams();
    if (query.page) params = params.set('page', String(query.page));
    if (query.numRecord) params = params.set('numRecord', String(query.numRecord));
    if (query.searchString) params = params.set('searchString', String(query.searchString));

    const url = `${config.API_URL}v1/auth/admin/chain-groups/search/restaurants`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders(), params });
  }

  getChainGroupRestaurants(chainId: string, query: { page?: number; numRecord?: number; searchString?: string; sortBy?: string; sortOrder?: 'asc' | 'desc' }) {
    let params = new HttpParams();
    if (query.page) params = params.set('page', String(query.page));
    if (query.numRecord) params = params.set('numRecord', String(query.numRecord));
    if (query.searchString) params = params.set('searchString', String(query.searchString));
    if (query.sortBy) params = params.set('sortBy', String(query.sortBy));
    if (query.sortOrder) params = params.set('sortOrder', String(query.sortOrder));

    const url = `${config.API_URL}v1/auth/admin/chain-groups/${chainId}/restaurants`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders(), params });
  }

  assignRestaurantsToChainGroup(chainId: string, payload: { restaurants: any[] }) {
    const url = `${config.API_URL}v1/auth/admin/chain-groups/${chainId}/restaurants`;
    return this.http.post<any>(url, payload, { headers: this.getAuthHeaders() });
  }

  removeRestaurantFromChainGroup(chainId: string, restaurantId: string) {
    const url = `${config.API_URL}v1/auth/admin/chain-groups/${chainId}/restaurants/${encodeURIComponent(restaurantId)}`;
    return this.http.delete<any>(url, { headers: this.getAuthHeaders() });
  }

  restoreRestaurantInChainGroup(chainId: string, restaurantId: string) {
    const url = `${config.API_URL}v1/auth/admin/chain-groups/${chainId}/restaurants/${encodeURIComponent(restaurantId)}/restore`;
    return this.http.put<any>(url, {}, { headers: this.getAuthHeaders() });
  }

  searchChainSystemUsers(query: { page?: number; numRecord?: number; searchString?: string; status?: boolean; user_role?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }) {
    let params = new HttpParams();
    if (query.page) params = params.set('page', String(query.page));
    if (query.numRecord) params = params.set('numRecord', String(query.numRecord));
    if (query.searchString) params = params.set('searchString', String(query.searchString));
    if (typeof query.status === 'boolean') params = params.set('status', String(query.status));
    if (query.user_role !== undefined && query.user_role !== null) params = params.set('user_role', String(query.user_role));
    if (query.sortBy) params = params.set('sortBy', String(query.sortBy));
    if (query.sortOrder) params = params.set('sortOrder', String(query.sortOrder));

    const url = `${config.API_URL}v1/auth/admin/chain-groups/search/system-users`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders(), params });
  }

  getChainGroupManagers(chainId: string, query: { page?: number; numRecord?: number; status?: boolean }) {
    let params = new HttpParams();
    if (query.page) params = params.set('page', String(query.page));
    if (query.numRecord) params = params.set('numRecord', String(query.numRecord));
    if (typeof query.status === 'boolean') params = params.set('status', String(query.status));

    const url = `${config.API_URL}v1/auth/admin/chain-groups/${chainId}/managers`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders(), params });
  }

  assignChainManager(chainId: string, payload: any) {
    const url = `${config.API_URL}v1/auth/admin/chain-groups/${chainId}/managers`;
    return this.http.post<any>(url, payload, { headers: this.getAuthHeaders() });
  }

  updateChainManager(chainId: string, managerId: string, payload: any) {
    const url = `${config.API_URL}v1/auth/admin/chain-groups/${chainId}/managers/${managerId}`;
    return this.http.put<any>(url, payload, { headers: this.getAuthHeaders() });
  }

  removeChainManager(chainId: string, managerId: string) {
    const url = `${config.API_URL}v1/auth/admin/chain-groups/${chainId}/managers/${managerId}`;
    return this.http.delete<any>(url, { headers: this.getAuthHeaders() });
  }

  restoreChainManager(chainId: string, managerId: string) {
    const url = `${config.API_URL}v1/auth/admin/chain-groups/${chainId}/managers/${managerId}/restore`;
    return this.http.put<any>(url, {}, { headers: this.getAuthHeaders() });
  }

  getChainSystemUserAccess(systemUserId: string) {
    const url = `${config.API_URL}v1/auth/admin/chain-groups/system-user/${systemUserId}/access`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders() });
  }
}
